// Section 15: aging by one season, the child -> adult (60 seasons) and adult -> elder (240)
// transitions, and status countdowns (grief, guest, restitution, custody).
import { forEachSorted } from '../../core/sorted';
import type { Countdown, Person, PhaseContext } from '../../core/types';

function tick<T extends Countdown>(c: T | null): T | null {
  if (c === null) return null;
  c.seasonsRemaining -= 1;
  return c.seasonsRemaining > 0 ? c : null;
}

function hasCountdown(p: Person): boolean {
  const s = p.status;
  return s.grieving !== null || s.guest !== null || s.restitution !== null || s.inCustody !== null;
}

export function age(ctx: PhaseContext): void {
  const { adultAtSeasons, elderAtSeasons } = ctx.cfg.people;
  const people = ctx.draft.people;
  // Everyone but this season's newborns (births run before aging: a newborn starts at age 0,
  // Section 15) was in the committed state, so iterate that (Section 2 budget: no sort of the
  // draft's keys) and skip whoever died earlier in the phase.
  forEachSorted(ctx.state.people, (snapshot) => {
    if (snapshot.origin === 'born' && snapshot.bornTurn === ctx.season.turn) return;
    const p = people[snapshot.id];
    if (p === undefined) return;
    p.ageSeasons += 1;
    const ticking = hasCountdown(snapshot);
    if (p.ageSeasons === adultAtSeasons) {
      ctx.report.add({ kind: 'came-of-age', personId: p.id });
      p.status.inSchool = false;
    }
    if (p.ageSeasons === elderAtSeasons) {
      ctx.report.add({ kind: 'became-elder', personId: p.id });
      if (p.job !== 'rest' && ctx.registry.jobs[p.job].heavy) {
        ctx.report.add({ kind: 'job-vacant', jobId: p.job, personId: p.id, reason: 'aged' });
        p.job = 'rest';
      }
    }
    if (!ticking) return;
    const s = p.status;
    s.grieving = tick(s.grieving);
    s.guest = tick(s.guest);
    s.restitution = tick(s.restitution);
    s.inCustody = tick(s.inCustody);
  });
}
